import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useIsPureAdmin } from '@/react/hooks/useUser';
import { getK8sAppliedPolicies } from '@/react/portainer/policies/utils/policy-types-metadata';

import { PageHeader } from '@@/PageHeader';
import { Widget, WidgetBody } from '@@/Widget';
import { Link } from '@@/Link';
import { TextTip } from '@@/Tip/TextTip';

import { KubernetesPolicySection } from './KubernetesPolicySection';

export function PoliciesView() {
  const environmentId = useEnvironmentId();
  const environmentQuery = useEnvironment(environmentId);
  const isPureAdmin = useIsPureAdmin();

  const policies = environmentQuery.data?.Policies;

  const appliedPolicies = getK8sAppliedPolicies(policies);
  const showEmptyState =
    !environmentQuery.isError &&
    (environmentQuery.isLoading || appliedPolicies.length === 0);

  return (
    <>
      <PageHeader
        title="Policies"
        breadcrumbs={[{ label: 'Fleet Governance Policies' }]}
        reload
      />

      <div className="row">
        <div className="col-sm-12">
          {environmentQuery.isError && (
            <TextTip color="red" className="mb-4">
              Failed to load policies for this environment. Please try again.
            </TextTip>
          )}

          {showEmptyState && (
            <Widget>
              <WidgetBody loading={environmentQuery.isLoading}>
                {!environmentQuery.isLoading && (
                  <div className="text-muted">
                    <p className="mb-2">
                      No policies are currently applied to this environment.
                    </p>
                    <p className="mb-2">
                      Policies allow you to enforce security settings, configure
                      cluster resources, manage RBAC, and control registry
                      access across multiple environments.
                    </p>
                    {isPureAdmin ? (
                      <p>
                        To create and manage policies, visit the{' '}
                        <Link
                          to="portainer.policies"
                          data-cy="policies-management-link"
                        >
                          Fleet Governance Policies
                        </Link>{' '}
                        page.
                      </p>
                    ) : (
                      <p>
                        Contact your Portainer administrator to enable policies
                        for this environment.
                      </p>
                    )}
                  </div>
                )}
              </WidgetBody>
            </Widget>
          )}

          {!environmentQuery.isError && appliedPolicies.length > 0 && (
            <div className="flex flex-col gap-4">
              {appliedPolicies.map((appliedPolicy) => (
                <KubernetesPolicySection
                  key={appliedPolicy.key}
                  item={appliedPolicy}
                  canEdit={isPureAdmin}
                  canExpand
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
